import { useState } from 'react';
import { parseTicketBarcode, DEFAULT_LAYOUT } from '@njpos/core';
import { api, usd } from '../lib/api';
import { ErrorNote, useLoad } from './ui';
import BarcodeScanner, { cameraScanSupported } from './BarcodeScanner';

// A shift or end-of-day audit: scan the front ticket of every pack in use, then compare what the packs sold with what the register recorded.
export default function LotteryAudit({ onDone }: { onDone?: () => void }) {
  const settings = useLoad(() => api<{ layout: typeof DEFAULT_LAYOUT }>('/api/lottery/settings'), []);
  const packs = useLoad(() => api<any[]>('/api/lottery/packs?status=active'), []);
  const layout = settings.data?.layout ?? DEFAULT_LAYOUT;
  const [codes, setCodes] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [cam, setCam] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const add = (raw: string) => {
    const c = raw.trim();
    if (!c) return;
    const t = parseTicketBarcode(c, layout);
    if (!t) { setErr(new Error(`Could not read “${c}”. It needs at least ${layout.gameDigits + layout.packDigits + layout.ticketDigits} digits.`)); return; }
    setErr(null);
    // scanning the same pack again replaces its ticket
    setCodes((cs) => [...cs.filter((x) => { const p = parseTicketBarcode(x, layout); return !(p && p.gameNo === t.gameNo && p.packNo === t.packNo); }), c]);
  };

  async function submit() {
    setBusy(true); setErr(null);
    try { setResult(await api('/api/lottery/audits', { body: { codes } })); setCodes([]); packs.reload(); onDone?.(); } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  if (result) return <AuditResult r={result} onClose={() => setResult(null)} />;

  const scanned = new Set(codes.map((c) => { const t = parseTicketBarcode(c, layout); return t ? `${t.gameNo}|${t.packNo}` : ''; }));
  const missing = (packs.data ?? []).filter((p) => !scanned.has(`${p.game_no}|${p.pack_no}`));
  return (
    <div className="stack">
      <p className="muted">Scan the barcode on the front ticket of every pack in use (the next ticket to sell). Do this at the end of every shift.</p>
      <form className="scanbar" onSubmit={(e) => { e.preventDefault(); add(text); setText(''); }}>
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Scan or type the ticket number" aria-label="Ticket barcode" inputMode="numeric" />
        <button className="primary big">Add</button>
      </form>
      {cameraScanSupported() && !cam && <button onClick={() => setCam(true)}>Scan with camera</button>}
      {cam && <BarcodeScanner onClose={() => setCam(false)} onCode={add} />}
      <ErrorNote error={err ?? settings.error} />
      <div className="panel">
        {codes.map((c) => {
          const t = parseTicketBarcode(c, layout)!;
          const pack = packs.data?.find((p) => p.game_no === t.gameNo && p.pack_no === t.packNo);
          return (
            <div key={c} className="m-line">
              <div className="grow"><div style={{ fontWeight: 600 }}>{pack?.game_name ?? `Game ${t.gameNo}`} · pack {t.packNo}{pack?.bin ? ` · bin ${pack.bin}` : ''}</div>
                <div className="faint">ticket {t.ticket}{pack ? '' : ' · not an active pack'}</div></div>
              <button className="ghost danger" aria-label="Remove" onClick={() => setCodes(codes.filter((x) => x !== c))}>✕</button>
            </div>);
        })}
        {codes.length === 0 && <div className="empty">Nothing scanned yet.</div>}
      </div>
      {codes.length > 0 && missing.length > 0 && (
        <div className="notice warn">Not scanned yet: {missing.map((p) => `${p.game_name} ${p.pack_no}${p.bin ? ` (bin ${p.bin})` : ''}`).join(', ')}</div>
      )}
      <button className="primary big" disabled={!codes.length || busy} onClick={submit}>{busy ? 'Checking…' : `Finish audit (${codes.length} pack${codes.length === 1 ? '' : 's'})`}</button>
    </div>
  );
}

function AuditResult({ r, onClose }: { r: any; onClose: () => void }) {
  const cls = r.standing === 'even' ? 'good' : 'bad';
  return (
    <div className="stack">
      <div className={`notice ${cls}`}>
        {r.standing === 'even' ? 'Even. The register matches the packs.' : `${r.standing === 'short' ? 'Short' : 'Over'} by ${usd(Math.abs(r.varianceCents))}.`}
      </div>
      <div className="stats">
        <div><div className="v">{usd(r.countedCents)}</div><div className="l">Packs say sold</div></div>
        <div><div className="v">{usd(r.recordedCents)}</div><div className="l">Register recorded</div></div>
        <div><div className="v">{r.packsScanned}</div><div className="l">Packs scanned</div></div>
      </div>
      <div className="panel"><div className="panel-head"><h2>Sold since the last audit</h2></div>
        <table className="grid"><thead><tr><th>Pack</th><th className="n">Tickets</th><th className="n">Value</th></tr></thead>
          <tbody>{r.lines.map((l: any) => <tr key={l.packId}><td>{l.game} <span className="faint">pack {l.packNo}{l.bin ? ` · bin ${l.bin}` : ''}{l.settled ? ' · finished' : ''}</span></td><td className="n">{l.tickets}</td><td className="n">{usd(l.cents)}</td></tr>)}</tbody></table>
      </div>
      {r.issues.length > 0 && (
        <div className="panel"><div className="panel-head"><h2>Needs attention</h2></div>
          {r.issues.map((i: any, n: number) => <div key={n} className="m-line"><span className={`chip ${i.severity === 'critical' ? 'bad' : 'warn'}`}>{i.kind.replace('_', ' ')}</span><div className="grow">{i.message}</div></div>)}
        </div>
      )}
      <button className="primary big" onClick={onClose}>Done</button>
    </div>
  );
}
